import { FilePlayer } from 'react-player/lib/players/FilePlayer';
import createSinglePlayer from 'react-player/lib/singlePlayer';
import axios from 'axios';
import HlsJS, { Events, ErrorData } from 'hls.js';
import IMaestroPlayerProps from '../models/IMaestroPlayerProps';
import IMaestroFilePlayer, {
  type HlsConstructor,
  type IMaestroFilePlayerState,
} from '../models/IMaestroFilePlayer';
import { getSDK } from '../utils';
import { MOBILE_MAX_WIDTH_PX } from 'style/constants';
import { SUBTITLES_OFF } from 'components/Video/video-player/Player/Context';

const HLS_GLOBAL = 'Hls';
const HLS_SDK_URL = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
const RETRY_PLAYBACK_INTERVAL_MS = 2000;

class MaestroFilePlayer extends FilePlayer implements IMaestroFilePlayer {
  static displayName = 'MaestroFilePlayer';

  hls: null | HlsJS = null;
  isAndroid = /Android/i.test(navigator.userAgent);
  isMobileDevice = /Mobi|Android/i.test(navigator.userAgent) || screen.width <= MOBILE_MAX_WIDTH_PX;
  removeAppleTextTrackEvents?: () => void;
  retryInterval: any = null;

  constructor(props: IMaestroPlayerProps) {
    super(props);
    this.state = {
      isLoaded: false,
      isLoading: false,
      retryAttempts: 0,
    } as IMaestroFilePlayerState;
    this.attemptLoading = this.attemptLoading.bind(this);
  }

  get nativeVideoSupported() {
    const isHLS = /.m3u8$/.test(this.props.url as string);
    const isMp4 = /.mp4$/.test(this.props.url as string);
    // falsy result from htmlVideoElement.canPlayType = ""
    const canPlayMp4 = ['probably', 'maybe'].includes(this.player.canPlayType('video/mp4'));
    const canPlayHLSType1 = ['probably', 'maybe'].includes(this.player.canPlayType('application/x-mpegURL'));
    const canPlayHLSType2 = ['probably', 'maybe'].includes(this.player.canPlayType('application/vnd.apple.mpegURL'));
    return (isMp4 && canPlayMp4) || (isHLS && (canPlayHLSType1 || canPlayHLSType2) && !this.isAndroid);
  }

  get videoPlayerContext() {
    return (this.props as IMaestroPlayerProps).videoPlayerContext;
  }

  attemptLoading() {
    if (this.getSecondsLoaded())
      return;

    const { url } = this.props;
    const { isLoaded } = this.state;
    const checkUrl = !isLoaded && /.m3u8$/.test(url as string);

    if (checkUrl) {
      this.setState((state: IMaestroFilePlayerState) => {
        return { ...state, retryAttempts: state.retryAttempts + 1 };
      });

      axios.get(this.props.url as string)
        .then(() => {
          this.initializePlayer();
          clearInterval(this.retryInterval);
        })
        .catch(() => null);
    } else {
      clearInterval(this.retryInterval);
      this.initializePlayer();
    }
  }

  componentDidMount() {
    // .m3u8 will 404 until 'active' webhook is received
    this.attemptLoading();
    this.retryInterval = setInterval(this.attemptLoading, RETRY_PLAYBACK_INTERVAL_MS);
  }

  componentDidUpdate(prevProps: IMaestroPlayerProps) {
    if (prevProps.isStreamActive !== this.props.isStreamActive && prevProps.url !== this.props.url) {
      this.setState({ isLoaded: false, isLoading: false, retryAttempts: 0 });

      if (this.retryInterval) {
        clearInterval(this.retryInterval);
      }

      this.retryInterval = setInterval(this.attemptLoading, RETRY_PLAYBACK_INTERVAL_MS);
    }

    const trackId = this.videoPlayerContext?.selectedSubtitle?.value;

    const hasSelectedSubtitleChanged = prevProps.selectedSubtitle !== trackId;

    if (hasSelectedSubtitleChanged) {
      this.updateSubtitleTrack();
    }
  }

  componentWillUnmount() {
    super.removeListeners(this.player);

    if (this.hls) {
      this.hls.destroy();
    }

    if (this.retryInterval) {
      clearInterval(this.retryInterval);
    }

    this.removeAppleTextTrackEvents?.();
  }

  initializePlayer() {
    super.addListeners(this.player);

    if (!this.state.isLoading) {
      this.setState({ isLoading: true });
      this.load(this.props.url as string);
    }

    this.setState({ isLoaded: true });
  }

  load(url: string) {
    const {
      config,
      onReady,
      onError,
    } = this.props;

    if (this.hls) {
      this.hls.destroy();
    }

    if (this.nativeVideoSupported) {
      this.player.src = url;
      this.setAppleTextTrackEvents();

      if (this.isMobileDevice && !this.isAndroid) {
        onReady?.();
      }
    } else {
      getSDK<HlsConstructor>(HLS_SDK_URL, HLS_GLOBAL)
        .then((Hls) => {
          if (Hls.isSupported()) {
            this.hls = new Hls(config!.file!.hlsOptions);

            this.hls.on(
              Hls.Events.ERROR as Events.ERROR,
              (e: Events.ERROR, data: ErrorData) => {
                switch (data.type) {
                  case Hls.ErrorTypes.MEDIA_ERROR: {
                    const { currentTime } = this.player;

                    if (data.details === 'fragParsingError') {
                      // when there's a broken fragment, we should let the player
                      // skip it to try and load the next one instead of force re-
                      // loading the same broken fragment. If it happens to be the
                      // very first fragment, we first try skipping 1 second
                      if (currentTime === 0) {
                        this.hls!.startLoad(1);
                      }

                      break;
                    }

                    this.hls!.detachMedia();
                    this.player.currentTime = currentTime;
                    this.hls!.attachMedia(this.player);
                    break;
                  }
                  default:
                    if (data.fatal) {
                      // cannot recover
                      this.hls!.destroy();
                    }
                    break;
                }

                onError!(e, data, this.hls, Hls);
              },
            );

            // Get subtitle track options for non-apple devices
            this.hls.on(Events.SUBTITLE_TRACKS_UPDATED, (_, data) => {
              this.videoPlayerContext?.setSubtitleOptions([
                SUBTITLES_OFF,
                ...data.subtitleTracks.map((track) => ({ label: track.name, value: track.id })),
              ]);
            });

            this.hls.loadSource(url);
            this.hls.attachMedia(this.player);

            onReady?.();
            this.setState({ isLoading: false });
          } else {
            super.load(url);
          }
        });
    }
  }

  seekTo(seconds: number | null) {
    if (seconds) {
      if (this.isMobileDevice) {
        if (this.player) {
          this.player.currentTime = seconds;
        }
      } else {
        this.player.currentTime = seconds;
      }
    }
  }

  setAppleTextTrackEvents() {
    // Get subtitle track options for apple devices
    const onTrackAdded = (e: TrackEvent) => {
      e.track!.mode = 'hidden';
      this.videoPlayerContext?.setSubtitleOptions([
        SUBTITLES_OFF,
        ...Object.entries(this.player.textTracks).map(([id, track]) => ({
          label: track.label,
          value: Number(id),
        })),
      ]);
    };

    // This keeps subtitles in sync when a change is made from the native apple video controls in fullscreen
    const onTrackListChange = () => {
      const tracks = [...this.player.textTracks];
      // When you pick a subtitle while in fullscreen, the subtitle previously set from custom controls is
      // not removed (we get multiple subtitles showing). So we need to find what was the new selection
      // this way since we also don't get that info in the event or anywhere else
      const newSelection = tracks.findIndex(
        (track, id) => {
          if (track.mode === 'showing' && id !== this.videoPlayerContext?.selectedSubtitle.value) {
            return true;
          }
        },
      ) + 1; // We add 1 because the first subtitle option in custom controls corresponds to "off"

      // When all the tracks are disabled it means "off" was selected, so if that's not the case we skip
      if (!newSelection && tracks.some(track => track.mode === 'showing')) {
        return;
      }

      this.videoPlayerContext?.setSelectedSubtitle(this.videoPlayerContext.subtitleOptions[newSelection]);
    };

    this.player.textTracks.addEventListener('addtrack', onTrackAdded);
    this.player.textTracks.addEventListener('change', onTrackListChange);

    this.removeAppleTextTrackEvents = () => {
      this.player.textTracks.removeEventListener('addtrack', onTrackAdded);
      this.player.textTracks.removeEventListener('change', onTrackListChange);
    };
  }

  updateSubtitleTrack() {
    const trackId = this.videoPlayerContext?.selectedSubtitle.value;

    if (!trackId) {
      return;
    }

    if (this.hls && trackId !== this.hls.subtitleTrack) {
      this.hls.subtitleTrack = trackId;
    }

    const tracks = this.player ? [...this.player.textTracks] : [];

    if (this.nativeVideoSupported && (trackId === SUBTITLES_OFF.value || tracks[trackId].mode !== 'showing')) {
      tracks.forEach((track, i) => {
        track.mode = i === trackId ? 'showing' : 'hidden';
      });
    }
  }
}

export default createSinglePlayer(MaestroFilePlayer as any) as any;
